import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Check, Loader2, MoreHorizontal, Pencil, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import DeleteWithReasonDialog from "../components/DeleteWithReasonDialog";
import { ACTION_HINTS, ACTION_LABELS, CATEGORIES, HISTORY_LABELS, REASON_ACTIONS, REVIEW_PERIODS, STATUS_LABELS, STATUS_TONES, knowledgeError } from "../lib/knowledge";
import { formatDay } from "../lib/governance";
import { formatDateTime } from "../lib/tickets";
import { Chip, Field } from "../components/governance/GovernanceBits";
import { Sections } from "../components/knowledge/ArticleText";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  ResponsiveDropdownMenu as DropdownMenu, ResponsiveDropdownMenuContent as DropdownMenuContent, ResponsiveDropdownMenuItem as DropdownMenuItem,
  ResponsiveDropdownMenuTrigger as DropdownMenuTrigger,
} from "../components/ui/responsive-dropdown-menu";

const DONE_TOASTS = { "new-draft": "Brouillon créé", "discard-draft": "Brouillon écarté", publish: "Article publié", review: "Article relu", archive: "Article archivé", unarchive: "Article réactivé" };

function ActionDialog({ action, article, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const needsReason = REASON_ACTIONS.includes(action);
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/knowledge/articles/${article.id}/${action}`, { expected_version: article.version, ...(needsReason ? { reason: reason.trim() } : {}) });
      toast.success(DONE_TOASTS[action]);
      onDone(data, action);
    } catch (e) {
      setError(knowledgeError(e, "Impossible de continuer. Réessayez."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open && !busy) onClose(); }}>
      <DialogContent data-testid={`article-dialog-${action}`}>
        <DialogHeader><DialogTitle>{ACTION_LABELS[action]} — {article.number}</DialogTitle><DialogDescription>{ACTION_HINTS[action]}</DialogDescription></DialogHeader>
        {needsReason && <div className="space-y-1.5"><Label htmlFor="kb-reason">Motif</Label><Textarea id="kb-reason" rows={3} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="article-reason" /></div>}
        {error && <p className="text-sm text-red-600" role="alert" data-testid="article-action-error">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Retour</Button>
          <Button variant={action === "archive" || action === "discard-draft" ? "destructive" : "default"} onClick={submit} disabled={busy || (needsReason && reason.trim().length < 3)} data-testid="article-action-submit">
            {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{ACTION_LABELS[action]}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MetaDialog({ article, onClose, onDone }) {
  const [category, setCategory] = useState(article.category);
  const [months, setMonths] = useState(article.review_months);
  const [owner, setOwner] = useState(article.owner.id);
  const [owners, setOwners] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { api.get("/knowledge/owner-options").then(({ data }) => setOwners(data.items || [])).catch(() => {}); }, []);
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.patch(`/knowledge/articles/${article.id}`, { expected_version: article.version, category, review_months: months, owner_id: owner });
      toast.success("Informations enregistrées");
      onDone(data);
    } catch (e) {
      setError(knowledgeError(e, "Enregistrement impossible. Réessayez."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open && !busy) onClose(); }}>
      <DialogContent data-testid="article-meta-dialog">
        <DialogHeader><DialogTitle>Informations — {article.number}</DialogTitle><DialogDescription>Le propriétaire répond de l'article : c'est à lui qu'on demande de le relire.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Catégorie</Label>
            <Select value={category} onValueChange={setCategory}><SelectTrigger className="h-10" aria-label="Catégorie" data-testid="meta-category"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map(([id, label]) => <SelectItem key={id} value={id}>{label}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-1.5"><Label>Propriétaire</Label>
            <Select value={owner} onValueChange={setOwner}><SelectTrigger className="h-10" aria-label="Propriétaire" data-testid="meta-owner"><SelectValue /></SelectTrigger>
              <SelectContent>{owners.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-1.5"><Label>Relecture</Label>
            <Select value={String(months)} onValueChange={(v) => setMonths(Number(v))}><SelectTrigger className="h-10" aria-label="Relecture" data-testid="meta-period"><SelectValue /></SelectTrigger>
              <SelectContent>{REVIEW_PERIODS.map(([n, label]) => <SelectItem key={n} value={String(n)}>{label}</SelectItem>)}</SelectContent></Select></div>
          {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose} disabled={busy}>Retour</Button><Button onClick={submit} disabled={busy} data-testid="meta-submit">{busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Enregistrer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function KnowledgeArticle() {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [article, setArticle] = useState(null);
  const [phase, setPhase] = useState("loading");
  const [dialog, setDialog] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/knowledge/articles/${articleId}`);
      setArticle(data);
      setPhase("ready");
    } catch (e) {
      setPhase(e?.response?.status === 404 ? "missing" : "error");
    }
  }, [articleId]);
  useEffect(() => { setPhase("loading"); load(); }, [load]);

  if (phase === "loading") return <div className="space-y-3"><Skeleton className="h-8 w-72" /><Skeleton className="h-40 w-full" /></div>;
  if (phase === "missing") {
    return (
      <Card className="p-8 text-center space-y-2" data-testid="article-missing">
        <h2 className="font-medium text-slate-900">Article introuvable</h2>
        <p className="text-sm text-slate-500">Il n'existe pas, n'est pas publié, ou vous n'y avez pas accès.</p>
        <Button asChild variant="outline"><Link to="/support/connaissances">Retour à la base de connaissance</Link></Button>
      </Card>
    );
  }
  if (phase === "error") {
    return (
      <Card className="p-6 text-center space-y-3" data-testid="article-error">
        <AlertTriangle className="mx-auto text-amber-500" size={20} />
        <p className="text-sm text-slate-700">Impossible de charger cet article.</p>
        <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
      </Card>
    );
  }

  const cap = article.capabilities;
  const manage = cap.manage;
  const actions = cap.actions || [];
  const shown = article.published || article.draft; // a never-published article is shown from its draft, to those who may see it
  const canDelete = article.status === "draft" && hasPermission(user, "knowledge:delete");
  const primary = actions.includes("publish") ? "publish" : actions.includes("new-draft") ? "new-draft" : null;
  const others = actions.filter((a) => a !== primary && a !== "new-draft" && a !== "publish");
  const done = (data, action) => {
    setDialog(null);
    setArticle(data);
    if (action === "new-draft") navigate(`/support/connaissances/${data.id}/modifier`);
  };

  return (
    <div className="space-y-4" data-testid="article-page">
      <div>
        <Link to="/support/connaissances" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft size={14} />Base de connaissance</Link>
        <div className="mt-1 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs text-slate-500 tabular-nums" data-testid="article-number">{article.number} · {article.category_label}</p>
            <h1 className="font-display font-bold text-2xl text-slate-900 break-words" data-testid="article-title">{shown.title}</h1>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              {article.status !== "published" && <Chip label={STATUS_LABELS[article.status]} tone={STATUS_TONES[article.status]} testid="article-status" />}
              {article.to_review && <Chip label="À relire" tone="amber" testid="article-to-review" />}
              {manage && article.has_draft && article.status === "published" && <Chip label="Brouillon en cours" tone="amber" testid="article-has-draft" />}
            </div>
          </div>
          {manage && (
            <div className="flex items-center gap-2">
              {primary && <Button onClick={() => setDialog(primary)} data-testid={`article-${primary}`}><Check size={15} className="mr-1.5" />{ACTION_LABELS[primary]}</Button>}
              {cap.edit_draft && <Button asChild variant="outline" data-testid="article-edit"><Link to={`/support/connaissances/${article.id}/modifier`}><Pencil size={14} className="mr-1.5" />Modifier le brouillon</Link></Button>}
              <DropdownMenu>
                <DropdownMenuTrigger asChild><Button variant="outline" size="icon" aria-label="Autres actions" data-testid="article-more"><MoreHorizontal size={16} /></Button></DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {cap.edit_meta && <DropdownMenuItem onClick={() => setDialog("meta")} data-testid="article-meta">Informations</DropdownMenuItem>}
                  {others.map((a) => <DropdownMenuItem key={a} onClick={() => setDialog(a)} className={a === "archive" || a === "discard-draft" ? "text-red-600" : undefined} data-testid={`article-${a}`}>{ACTION_LABELS[a]}</DropdownMenuItem>)}
                  {canDelete && <DropdownMenuItem onClick={() => setDialog("delete")} className="text-red-600 focus:text-red-700" data-testid="article-delete">Supprimer l'article…</DropdownMenuItem>}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
        {manage && article.draft && article.status === "published" && <p className="mt-2 text-sm text-amber-800" data-testid="article-draft-note">Un brouillon est en cours de rédaction : les lecteurs voient toujours la version publiée ci-dessous.</p>}
        {manage && article.status === "draft" && <p className="mt-2 text-sm text-amber-800" data-testid="article-unpublished-note">Cet article n'est pas encore publié : seuls ceux qui peuvent le rédiger le voient.</p>}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_20rem] items-start">
        <div className="space-y-4 min-w-0">
          <Card className="p-4 sm:p-6" data-testid="article-body">
            {shown.sections.length === 0 ? <p className="text-sm text-slate-500">Cet article n'a pas encore de contenu.</p> : <Sections sections={shown.sections} />}
          </Card>
          {manage && (
            <Card className="p-4 sm:p-5 space-y-3" data-testid="article-history">
              <h2 className="text-base font-semibold text-slate-900">Versions et historique</h2>
              <ul className="space-y-1 text-sm" data-testid="article-versions">
                {article.versions.map((v) => <li key={v.id} className="flex flex-wrap gap-x-2 text-slate-700"><span className="tabular-nums">Version {v.number}</span><span className="text-slate-500">{{ published: "publiée", replaced: "remplacée", draft: "brouillon", discarded: "écartée" }[v.status] || v.status}{v.published_at ? ` · ${formatDay(v.published_at.slice(0, 10))}` : ""}{v.published_by ? ` · ${v.published_by}` : ""}</span></li>)}
              </ul>
              <ol className="space-y-2 border-t border-slate-100 pt-3">
                {article.history.map((h) => (
                  <li key={h.id} className="border-l-2 border-slate-200 pl-3">
                    <div className="text-sm text-slate-900">{HISTORY_LABELS[h.kind] || h.kind}</div>
                    <div className="text-xs text-slate-500">{formatDateTime(h.at)}{h.actor_name ? ` · ${h.actor_name}` : ""}</div>
                    {h.reason && <div className="text-xs text-slate-600">Motif : {h.reason}</div>}
                  </li>
                ))}
              </ol>
            </Card>
          )}
        </div>
        <div className="space-y-4 lg:sticky lg:top-4">
          {shown.sections.filter((s) => s.title).length > 1 && (
            <Card className="p-4 hidden lg:block" data-testid="article-toc">
              <h2 className="text-base font-semibold text-slate-900 mb-2">Dans cet article</h2>
              <ul className="space-y-1 text-sm">{shown.sections.filter((s) => s.title).map((s) => <li key={s.id}><a href={`#s-${s.id}`} className="text-slate-700 hover:text-jamora">{s.title}</a></li>)}</ul>
            </Card>
          )}
          <Card className="p-4" data-testid="article-side">
            <h2 className="text-base font-semibold text-slate-900 mb-1">Fiche</h2>
            <dl className="divide-y divide-slate-100">
              <Field label="Propriétaire">{article.owner.name || "—"}</Field>
              <Field label="Catégorie">{article.category_label}</Field>
              {article.published && <Field label="Version">{article.published.number}{article.published.published_at ? ` · ${formatDay(article.published.published_at.slice(0, 10))}` : ""}</Field>}
              {article.last_review_on && <Field label="Dernière relecture">{formatDay(article.last_review_on)}</Field>}
              {article.next_review_on && <Field label="Prochaine relecture"><span data-testid="article-next-review">{formatDay(article.next_review_on)}</span></Field>}
              {shown.tags.length > 0 && <Field label="Mots-clés">{shown.tags.join(", ")}</Field>}
            </dl>
          </Card>
        </div>
      </div>
      {dialog === "meta" && <MetaDialog article={article} onClose={() => setDialog(null)} onDone={(data) => { setDialog(null); setArticle(data); }} />}
      {dialog && dialog !== "meta" && dialog !== "delete" && <ActionDialog action={dialog} article={article} onClose={() => setDialog(null)} onDone={done} />}
      <DeleteWithReasonDialog
        target={dialog === "delete" ? article : null} testId="delete-governance" onClose={() => setDialog(null)}
        title={`Supprimer l'article ${article.number} ?`}
        description="Il va dans la corbeille et se restaure pendant 30 jours. Un article déjà publié s'archive à la place."
        onConfirm={async (reason) => {
          try {
            await api.delete(`/knowledge/articles/${article.id}`, { data: { reason } });
            toast.success("Mis à la corbeille");
            navigate("/support/connaissances");
          } catch (e) {
            toast.error(knowledgeError(e, "Impossible de supprimer cet élément."));
            setDialog(null);
          }
        }}
      />
    </div>
  );
}
